import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FiImage, FiSave, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import API from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const MAX_IMAGES = 8;

const buildImageUrl = (img) => {
  if (!img) return '';
  if (typeof img !== 'string') return '';
  if (/^https?:\/\//i.test(img)) return img;
  const base = API.defaults?.baseURL || (process.env.REACT_APP_API_URL || '');
  let s = String(img).trim();
  if (s.startsWith('/uploads/')) s = s.replace(/^\/uploads\//, '/api/uploads/');
  if (s.startsWith('uploads/')) s = s.replace(/^uploads\//, '/api/uploads/');
  if (!s.startsWith('/api/uploads') && !s.startsWith('/')) s = `/api/uploads/${s.replace(/^\/+/, '')}`;
  try { return new URL(s, base || window.location.origin).toString(); } catch { return s; }
};

const normalizeDealIncoming = (d) => {
  if (!d || typeof d !== 'object') return {};
  const description = d.description ?? d.desc ?? d.body ?? '';
  const originalPrice = d.originalPrice ?? d.original_price ?? d.origPrice ?? d.price ?? '';
  const discountedPrice = d.discountedPrice ?? d.discounted_price ?? d.discounted ?? d.price ?? '';
  let category = '';
  if (d.category) {
    if (typeof d.category === 'string') category = d.category;
    else category = d.category._id || d.category.id || d.category.slug || d.category.name || '';
  }
  const startDate = d.startDate ?? d.start_date ?? d.validFrom ?? d.valid_from ?? d.from ?? '';
  const endDate = d.endDate ?? d.end_date ?? d.validUntil ?? d.valid_until ?? d.until ?? '';
  let locationVal = '';
  if (Array.isArray(d.locations) && d.locations.length) locationVal = d.locations[0];
  else if (d.location) locationVal = d.location;
  else if (d.address) locationVal = d.address;
  else if (Array.isArray(d.addresses) && d.addresses.length) locationVal = d.addresses[0];
  let terms = '';
  if (Array.isArray(d.conditions) && d.conditions.length) terms = d.conditions.join('\n');
  else if (d.conditions && typeof d.conditions === 'string') terms = d.conditions;
  else if (Array.isArray(d.terms) && d.terms.length) terms = d.terms.join('\n');
  else if (d.terms && typeof d.terms === 'string') terms = d.terms;
  let highlights = [''];
  try {
    if (Array.isArray(d.highlights) && d.highlights.length) highlights = d.highlights;
    else if (d.highlights && typeof d.highlights === 'string') {
      try {
        const parsed = JSON.parse(d.highlights);
        if (Array.isArray(parsed)) highlights = parsed;
        else highlights = [String(parsed)];
      } catch (_) {
        highlights = d.highlights.split('\n').map(s => s.trim()).filter(Boolean);
      }
    } else if (d.points || d.pointsFort || d.featureList) {
      const alt = d.points || d.pointsFort || d.featureList;
      if (Array.isArray(alt)) highlights = alt;
      else if (typeof alt === 'string') highlights = alt.split('\n').map(s => s.trim()).filter(Boolean);
    }
    if (!highlights || !highlights.length) highlights = [''];
  } catch (e) { highlights = ['']; }

  const imgsRaw = Array.isArray(d.images) ? d.images : (d.image ? (Array.isArray(d.image) ? d.image : [d.image]) : []);
  const images = imgsRaw.map(i => {
    if (!i) return '';
    if (typeof i === 'string') return i;
    if (i.url) return i.url;
    if (i.path) return i.path;
    if (i.filename) return i.filename;
    return '';
  }).filter(Boolean);

  const quantity = d.quantity ?? d.qty ?? 0;
  const title = d.title ?? d.name ?? '';
  return {
    title,
    description,
    originalPrice,
    price: discountedPrice,
    category,
    validFrom: startDate,
    validUntil: endDate,
    quantity,
    location: locationVal,
    terms,
    highlights,
    images,
  };
};

const EditDeal = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const mountedRef = useRef(true);

  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    originalPrice: '',
    price: '',
    category: '',
    validFrom: '',
    validUntil: '',
    quantity: '',
    location: '',
    terms: '',
    highlights: [''],
    images: [], // mix: existing urls (string) + new File objects appended for form convenience (we manage separately)
  });
  const [errors, setErrors] = useState({});
  const imagePreviewsRef = useRef([]);
  const [existingImages, setExistingImages] = useState([]); // urls
  const [newFiles, setNewFiles] = useState([]);

  useEffect(() => {
    mountedRef.current = true;
    const incoming = location?.state?.deal;
    if (incoming && mountedRef.current) {
      applyIncoming(incoming);
    } else if (editId) {
      fetchDealById(editId);
    }
    return () => {
      mountedRef.current = false;
      imagePreviewsRef.current.forEach(u => { try { URL.revokeObjectURL(u); } catch (e) {} });
      imagePreviewsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state, editId]);

  const applyIncoming = (d) => {
    const n = normalizeDealIncoming(d);
    const fmtDate = (v) => {
      if (!v) return '';
      try { const dt = new Date(v); if (isNaN(dt)) return ''; return dt.toISOString().slice(0,10); } catch { return ''; }
    };
    const preset = {
      title: n.title || '',
      description: n.description || '',
      originalPrice: n.originalPrice ?? '',
      price: n.price ?? '',
      category: n.category || '',
      validFrom: fmtDate(n.validFrom),
      validUntil: fmtDate(n.validUntil),
      quantity: n.quantity ?? '',
      location: n.location || '',
      terms: n.terms || '',
      highlights: Array.isArray(n.highlights) && n.highlights.length ? n.highlights : [''],
    };
    const imgs = Array.isArray(n.images) ? n.images : [];
    if (!mountedRef.current) return;
    setFormData(prev => ({ ...prev, ...preset, images: imgs }));
    setExistingImages(imgs);
    imagePreviewsRef.current = imgs.map(buildImageUrl);
  };

  const fetchDealById = async (id) => {
    setLoading(true);
    try {
      // try admin endpoint first, then public
      let res = null;
      try { res = await API.get(`/admin/deals/${id}`); }
      catch (e1) {
        try { res = await API.get(`/deals/id/${id}`); }
        catch (e2) {
          try { res = await API.get(`/merchant/deals/${id}`); } catch (e3) { throw e3; }
        }
      }
      const payload = res?.data?.data ?? res?.data ?? res;
      const deal = Array.isArray(payload) ? payload[0] : payload;
      if (deal) applyIncoming(deal);
      else throw new Error('Deal introuvable');
    } catch (err) {
      console.error('fetchDealById', err);
      toast.error('Impossible de charger le deal');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleHighlightChange = (i, v) => {
    const arr = [...formData.highlights]; arr[i] = v; setFormData(prev => ({ ...prev, highlights: arr }));
  };
  const addHighlight = () => setFormData(prev => ({ ...prev, highlights: [...prev.highlights, ''] }));
  const removeHighlight = (i) => setFormData(prev => ({ ...prev, highlights: prev.highlights.filter((_, idx) => idx !== i) }));

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const totalExisting = existingImages.length + newFiles.length;
    const spaceLeft = Math.max(0, MAX_IMAGES - totalExisting);
    const allowed = files.slice(0, spaceLeft).filter(f => {
      if (f.size > 5 * 1024 * 1024) { toast.warn(`${f.name} ignoré (trop gros)`); return false; }
      return true;
    });
    if (!allowed.length) { e.target.value = null; return; }
    const urls = allowed.map(f => URL.createObjectURL(f));
    imagePreviewsRef.current = [...imagePreviewsRef.current, ...urls];
    setNewFiles(prev => [...prev, ...allowed]);
    setFormData(prev => ({ ...prev, images: [...(prev.images || []), ...allowed] }));
    setErrors(prev => ({ ...prev, images: undefined }));
    e.target.value = null;
  };

  const removeImageAt = (index) => {
    const totalExisting = existingImages.length;
    if (index < totalExisting) {
      const newExisting = existingImages.filter((_, i) => i !== index);
      setExistingImages(newExisting);
      setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
      try { URL.revokeObjectURL(imagePreviewsRef.current[index]); } catch (e) {}
      imagePreviewsRef.current = imagePreviewsRef.current.filter((_, i) => i !== index);
    } else {
      const newFileIndex = index - totalExisting;
      try { URL.revokeObjectURL(imagePreviewsRef.current[totalExisting + newFileIndex]); } catch (e) {}
      imagePreviewsRef.current = imagePreviewsRef.current.filter((_, i) => i !== (totalExisting + newFileIndex));
      setNewFiles(prev => prev.filter((_, i) => i !== newFileIndex));
      setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== (totalExisting + newFileIndex)) }));
    }
  };

  const calculateDiscount = () => {
    const orig = parseFloat(formData.originalPrice) || 0;
    const cur = parseFloat(formData.price) || 0;
    if (orig > 0 && cur > 0 && cur < orig) return Math.round(((orig - cur) / orig) * 100);
    return 0;
  };

  const validate = () => {
    const e = {};
    if (!formData.title?.trim()) e.title = 'Titre requis';
    if (!formData.description?.trim()) e.description = 'Description requise';
    const orig = parseFloat(formData.originalPrice);
    const price = parseFloat(formData.price);
    if (!Number.isFinite(orig) || orig < 0) e.originalPrice = 'Prix original invalide';
    if (!Number.isFinite(price) || price < 0) e.price = 'Prix invalide';
    if (Number.isFinite(orig) && Number.isFinite(price) && price > orig) e.price = 'Le prix doit être <= prix original';
    if (!formData.category) e.category = 'Catégorie requise';
    if (!formData.location?.trim()) e.location = 'Localisation requise';
    if (!formData.quantity || parseInt(formData.quantity, 10) < 0) e.quantity = 'Quantité invalide';
    if (formData.validFrom && formData.validUntil && new Date(formData.validFrom) > new Date(formData.validUntil)) e.validUntil = 'Date fin doit être après début';
    const totalImages = (existingImages?.length || 0) + (newFiles?.length || 0);
    if (totalImages === 0) e.images = 'Au moins une image requise';
    if (totalImages > MAX_IMAGES) e.images = `Max ${MAX_IMAGES} images`;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error('Corrige les erreurs'); return; }
    if (!editId) { toast.error('ID manquant'); return; }
    setLoading(true);
    setUploadProgress(null);

    try {
      const payload = new FormData();
      payload.append('title', (formData.title || '').trim());
      payload.append('description', (formData.description || '').trim());
      payload.append('originalPrice', String(Number(formData.originalPrice || 0)));
      payload.append('discountedPrice', String(Number(formData.price || 0)));
      payload.append('price', String(Number(formData.price || 0)));
      payload.append('category', formData.category || '');
      payload.append('quantity', String(Math.max(0, parseInt(formData.quantity || '0', 10))));
      payload.append('location', formData.location || '');
      if (formData.validFrom) { payload.append('validFrom', formData.validFrom); payload.append('startDate', new Date(formData.validFrom).toISOString()); }
      if (formData.validUntil) { payload.append('validUntil', formData.validUntil); payload.append('endDate', new Date(formData.validUntil).toISOString()); }
      payload.append('terms', formData.terms || '');
      const highlights = (formData.highlights || []).map(h => h.trim()).filter(Boolean);
      if (highlights.length) payload.append('highlights', JSON.stringify(highlights));

      // existing image urls to keep
      (existingImages || []).forEach(url => payload.append('existingImages', url));
      // new files
      (newFiles || []).forEach(f => payload.append('images', f));

      const config = {
        onUploadProgress: (evt) => {
          if (evt && evt.total) {
            const pct = Math.round((evt.loaded * 100) / evt.total);
            if (mountedRef.current) setUploadProgress(pct);
          }
        }
      };

      // prefer admin update route if admin user; try several fallbacks
      let res;
      const tryEndpoints = [];
      if (user && (typeof user.role === 'string' ? user.role.toLowerCase().includes('admin') : (Array.isArray(user.roles) && user.roles.some(r => (typeof r === 'string' ? r : r?.name || '').toLowerCase().includes('admin'))))) {
        tryEndpoints.push(`/admin/deals/${editId}`, `/admin/deals/id/${editId}`);
      }
      tryEndpoints.push(`/merchant/deals/${editId}`, `/deals/id/${editId}`, `/deals/${editId}`);

      let lastErr;
      for (const ep of tryEndpoints) {
        try {
          res = await API.put(ep, payload, config);
          break;
        } catch (e) { lastErr = e; }
      }
      if (!res) throw lastErr;

      const data = res?.data ?? res;
      if (data?.success) {
        toast.success(data.message || 'Deal mis à jour');
        if (!mountedRef.current) return;
        navigate((user && (typeof user.role === 'string' ? user.role.toLowerCase().includes('admin') : (Array.isArray(user.roles) && user.roles.some(r => (typeof r === 'string' ? r : r?.name || '').toLowerCase().includes('admin'))))) ? '/admin/deals' : '/merchant/deals');
      } else {
        toast.error(data?.message || 'Erreur mise à jour');
        if (data?.errors) setErrors(prev => ({ ...prev, ...data.errors }));
      }
    } catch (err) {
      console.error('update error', err);
      const msg = err?.response?.data?.message || err?.message || 'Erreur';
      const srv = err?.response?.data?.errors || err?.response?.data || {};
      setErrors(prev => ({ ...prev, ...srv }));
      toast.error(msg);
    } finally {
      if (mountedRef.current) { setLoading(false); setUploadProgress(null); }
    }
  };

  const combinedImagesForRender = () => {
    const existingPreviews = existingImages.map(buildImageUrl);
    const newPreviews = newFiles.map((f, i) => {
      const idx = existingImages.length + i;
      return imagePreviewsRef.current[idx] || (() => {
        const url = URL.createObjectURL(f);
        imagePreviewsRef.current[idx] = url;
        return url;
      })();
    });
    return [...existingPreviews, ...newPreviews];
  };

  useEffect(() => {
    if (existingImages.length && imagePreviewsRef.current.length === 0) {
      imagePreviewsRef.current = existingImages.map(buildImageUrl);
    }
  }, [existingImages]);

  const discount = calculateDiscount();

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Éditer le Deal</h1>
          <p className="text-gray-600 mt-1">Modifiez les informations</p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b"><h2 className="text-xl font-semibold">Informations Principales</h2></div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                <input name="title" value={formData.title} onChange={handleChange} className={`w-full px-4 py-2 border rounded-lg ${errors.title ? 'border-red-300' : 'border-gray-300'}`} />
                {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea name="description" value={formData.description} onChange={handleChange} rows="4" className={`w-full px-4 py-2 border rounded-lg ${errors.description ? 'border-red-300' : 'border-gray-300'}`} />
                {errors.description && <p className="mt-1 text-xs text-red-600">{errors.description}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
                  <input name="category" value={formData.category} onChange={handleChange} placeholder="slug ou id" className="w-full px-4 py-2 border rounded-lg" />
                  {errors.category && <p className="mt-1 text-xs text-red-600">{errors.category}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Localisation *</label>
                  <input name="location" value={formData.location} onChange={handleChange} className={`w-full px-4 py-2 border rounded-lg ${errors.location ? 'border-red-300' : 'border-gray-300'}`} />
                  {errors.location && <p className="mt-1 text-xs text-red-600">{errors.location}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* pricing, dates, highlights, images, terms similar to CreateDeal but using combinedImagesForRender */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b"><h2 className="text-xl font-semibold">Prix et Remise</h2></div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input type="number" name="originalPrice" value={formData.originalPrice} onChange={handleChange} placeholder="Prix original" className={`px-4 py-2 border rounded-lg ${errors.originalPrice ? 'border-red-300' : 'border-gray-300'}`} />
                <input type="number" name="price" value={formData.price} onChange={handleChange} placeholder="Prix deal" className={`px-4 py-2 border rounded-lg ${errors.price ? 'border-red-300' : 'border-gray-300'}`} />
                <div className="flex items-center h-11 px-4 bg-green-50 border border-green-200 rounded-lg"><span className="text-lg font-bold text-green-600">-{discount}%</span></div>
              </div>
              <div><input type="number" name="quantity" value={formData.quantity} onChange={handleChange} placeholder="Quantité" className={`w-full px-4 py-2 border rounded-lg ${errors.quantity ? 'border-red-300' : 'border-gray-300'}`} /></div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b"><h2 className="text-xl font-semibold">Période de Validité</h2></div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <input type="date" name="validFrom" value={formData.validFrom} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" />
              <input type="date" name="validUntil" value={formData.validUntil} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b flex justify-between">
              <h2 className="text-xl font-semibold">Points Forts</h2>
              <button type="button" onClick={addHighlight} className="px-3 py-1 bg-primary-600 text-white rounded">Ajouter</button>
            </div>
            <div className="p-6 space-y-3">
              {formData.highlights.map((h, i) => (
                <div key={i} className="flex items-center space-x-2">
                  <input value={h} onChange={(e) => handleHighlightChange(i, e.target.value)} className="flex-1 px-4 py-2 border rounded-lg" />
                  {formData.highlights.length > 1 && <button type="button" onClick={() => removeHighlight(i)} className="p-2 text-red-600"><FiX /></button>}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b"><h2 className="text-xl font-semibold">Images</h2></div>
            <div className="p-6">
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <FiImage className="mx-auto text-4xl text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 mb-4">Glissez vos images ou cliquez (max {MAX_IMAGES})</p>
                <input id="image-upload" type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" />
                <label htmlFor="image-upload" className="inline-block px-4 py-2 bg-gray-100 rounded cursor-pointer">Choisir des images</label>
                {errors.images && <p className="mt-2 text-xs text-red-600">{errors.images}</p>}

                <div className="mt-4 grid grid-cols-3 gap-3">
                  {combinedImagesForRender().map((url, idx) => (
                    <div key={idx} className="relative border rounded overflow-hidden">
                      {url ? <img src={url} alt={`preview-${idx}`} className="w-full h-32 object-cover" /> : <div className="w-full h-32 bg-gray-100 flex items-center justify-center">No image</div>}
                      <button type="button" onClick={() => removeImageAt(idx)} className="absolute top-1 right-1 bg-white rounded-full p-1 text-red-600"><FiX /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b"><h2 className="text-xl font-semibold">Conditions</h2></div>
            <div className="p-6">
              <textarea name="terms" value={formData.terms} onChange={handleChange} rows="4" className="w-full px-4 py-2 border rounded-lg" />
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button type="button" onClick={() => navigate((user && (typeof user.role === 'string' ? user.role.toLowerCase().includes('admin') : false)) ? '/admin/deals' : '/merchant/deals')} className="px-6 py-3 border rounded">Annuler</button>
            <button type="submit" disabled={loading} className="flex items-center px-6 py-3 bg-primary-600 text-white rounded">
              {loading ? (uploadProgress !== null ? `Envoi ${uploadProgress}%` : 'Envoi...') : (<><FiSave className="mr-2" />Enregistrer</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDeal;
